import React, {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ClickAwayListener, Paper, Popper, Typography } from "@mui/material";
import { ClientStatusCardViewModel } from "@finpay-development/shared-platform/lib/esm/shared/model/client-status-card";
import { usePatientSearch } from "@finpay-development/shared-platform/lib/esm/shared/patient-search/usePatientSearch";

import classes from "./global-search.module.css";
import GlobalSearchInput from "./global-search-input";
import GlobalSearchHelp from "./global-search-help";
import GlobalSearchRecent from "./global-search-recent";
import GlobalSearchClientFilter from "./global-search-client-filter";
import GlobalSearchPatientList from "./global-search-patient-list";
import GlobalSearchPatientDetail from "./global-search-patient-detail";
import {
  GlobalSearchAccess,
  narrowPatientToClients,
  patientMatchesClients,
  useGlobalSearchAccess,
} from "./global-search-utils";

const MIN_LENGTH = 3;
const DEFAULT_ROWS_PER_PAGE = 10;
/** While a client filter is active, fetch this many rows and page through the filtered set locally. */
const FILTER_BATCH_SIZE = 100;
const placeholderFor = ({ finAdvisor, paymentHub }: GlobalSearchAccess): string => {
  if (finAdvisor && paymentHub) return "Search for a patient, VOB, estimate, IOC, or payment hub...";
  if (finAdvisor) return "Search for a patient, VOB, estimate, or IOC...";
  return "Search for a patient or payment hub account...";
};

/** usePatientSearch only exposes a TablePagination-style rows-per-page handler. */
const rowsPerPageEvent = (rows: number) =>
  ({ target: { value: String(rows) } } as ChangeEvent<HTMLInputElement>);

export interface GlobalSearchProps {
  placeholder?: string;
  /**
   * Override navigation to an app path (patient record, VOB, estimate, IOC).
   * Defaults to opening the path in a new tab, matching the existing patient
   * search behaviour.
   */
  onNavigate?: (path: string) => void;
}

/**
 * Single search bar for the top navigation. Renders a dropdown panel with:
 *  - a client filter pinned at the top,
 *  - recent searches and "Search by" help when the input is empty,
 *  - patient matches as the user types,
 *  - a detail pane (profile, VOBs, estimates, IOCs, …) when a patient is selected.
 */
const GlobalSearch = (props: GlobalSearchProps) => {
  const access = useGlobalSearchAccess();
  const { placeholder = placeholderFor(access), onNavigate } = props;

  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedClients, setSelectedClients] = useState<ClientStatusCardViewModel[]>([]);
  const [localPage, setLocalPage] = useState(0);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const {
    inputValue,
    setInputValue,
    searchTerm,
    patientsList,
    patientsCount,
    isLoading,
    page,
    rowsPerPage,
    handlePageChange,
    handleChangeRowsPerPage,
    searchNow,
    reset,
  } = usePatientSearch({
    paramId: -2,
    debounceMs: 500,
    minLength: MIN_LENGTH,
    rowsPerPageDefault: DEFAULT_ROWS_PER_PAGE,
    addTimingRiskFilter: false,
  });

  const clientIds = useMemo(
    () => new Set(selectedClients.map((c) => c.clientId)),
    [selectedClients]
  );
  const isFiltering = clientIds.size > 0;

  // Switch the hook between server paging (no filter) and one large batch (filter active).
  useEffect(() => {
    if (isFiltering && rowsPerPage !== FILTER_BATCH_SIZE) {
      handleChangeRowsPerPage(rowsPerPageEvent(FILTER_BATCH_SIZE));
    } else if (!isFiltering && rowsPerPage === FILTER_BATCH_SIZE) {
      handleChangeRowsPerPage(rowsPerPageEvent(localRowsPerPage));
    }
  }, [isFiltering, rowsPerPage, localRowsPerPage, handleChangeRowsPerPage]);

  useEffect(() => {
    setLocalPage(0);
  }, [searchTerm, selectedClients]);

  const filteredPatients = useMemo(
    () => patientsList.filter((p) => patientMatchesClients(p, clientIds)),
    [patientsList, clientIds]
  );

  const visiblePatients = isFiltering
    ? filteredPatients.slice(localPage * localRowsPerPage, (localPage + 1) * localRowsPerPage)
    : patientsList;
  const totalCount = isFiltering ? filteredPatients.length : patientsCount;
  const batchTruncated = isFiltering && patientsCount > patientsList.length;

  const selectedPatient = useMemo(() => {
    const match = filteredPatients.find((p) => p._id === selectedId);
    return match ? narrowPatientToClients(match, clientIds) : null;
  }, [filteredPatients, selectedId, clientIds]);

  // Drop the selection when it is no longer in the visible results.
  useEffect(() => {
    if (selectedId && !visiblePatients.some((p) => p._id === selectedId)) {
      setSelectedId(null);
    }
  }, [visiblePatients, selectedId]);

  const close = useCallback(() => {
    setOpen(false);
    setSelectedId(null);
  }, []);

  const clear = useCallback(() => {
    reset();
    setSelectedId(null);
  }, [reset]);

  const navigate = useCallback(
    (path: string) => {
      if (onNavigate) {
        onNavigate(path);
      } else {
        window.open(`${window.location.origin}${path}`, "_blank");
      }
      close();
    },
    [onNavigate, close]
  );

  const handleKeyDown = async (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      close();
      (event.target as HTMLElement).blur();
    } else if (event.key === "Enter") {
      await searchNow(true);
    }
  };

  const handleListPageChange = (event: unknown, newPage: number) => {
    if (isFiltering) setLocalPage(newPage);
    else handlePageChange(event, newPage);
  };

  const handleListRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLocalRowsPerPage(parseInt(event.target.value, 10));
    setLocalPage(0);
    if (!isFiltering) handleChangeRowsPerPage(event);
  };

  const trimmed = inputValue.trim();
  const showEmptyState = trimmed.length === 0;
  const showTooShort = !showEmptyState && trimmed.length < MIN_LENGTH;
  const hasResults = visiblePatients.length > 0;
  const showNoResults =
    !showEmptyState && !showTooShort && !hasResults && !isLoading && searchTerm.length >= MIN_LENGTH;
  const showSearching = !showEmptyState && !showTooShort && !hasResults && isLoading;

  return (
    <ClickAwayListener onClickAway={close} mouseEvent="onMouseDown" touchEvent="onTouchStart">
      <div className={classes.root} test-id="global-search">
        <GlobalSearchInput
          ref={anchorRef}
          value={inputValue}
          isLoading={isLoading}
          placeholder={placeholder}
          onChange={(e) => {
            setInputValue(e.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
          onClear={clear}
        />
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{ zIndex: (theme) => theme.zIndex.modal }}
        >
          <Paper
            elevation={6}
            className={`${classes.panel} ${selectedPatient ? classes.panelWide : ""}`}
            test-id="global-search-panel"
          >
            <GlobalSearchClientFilter
              value={selectedClients}
              onChange={setSelectedClients}
              results={showEmptyState ? [] : patientsList}
            />

            {showEmptyState && (
              <div className={classes.emptyColumns}>
                <div className={classes.emptyColumn}>
                  <GlobalSearchRecent
                    onSelect={(query) => {
                      setInputValue(query);
                      setOpen(true);
                    }}
                  />
                </div>
                <div className={classes.emptyColumn}>
                  <GlobalSearchHelp />
                </div>
              </div>
            )}

            {showTooShort && (
              <div className={classes.emptyState}>
                <Typography variant="body2">
                  Keep typing — at least {MIN_LENGTH} characters are needed to search.
                </Typography>
              </div>
            )}

            {showSearching && (
              <div className={classes.emptyState}>
                <Typography variant="body2">Searching…</Typography>
              </div>
            )}

            {showNoResults && (
              <div className={classes.emptyState}>
                <Typography variant="body2">
                  <b>
                    {isFiltering && patientsList.length > 0
                      ? "No matching results for the selected clients"
                      : "No matching results"}
                  </b>
                </Typography>
                <Typography variant="caption">
                  {isFiltering
                    ? "Try removing a client filter or changing your search."
                    : "Try a different name, email, phone number, or ID."}
                </Typography>
              </div>
            )}

            {hasResults && (
              <div className={classes.panelBody}>
                <div className={classes.listPane}>
                  <GlobalSearchPatientList
                    patients={visiblePatients}
                    totalCount={totalCount}
                    selectedId={selectedPatient?._id ?? null}
                    page={isFiltering ? localPage : page}
                    rowsPerPage={isFiltering ? localRowsPerPage : rowsPerPage}
                    onSelect={(patient) =>
                      setSelectedId((prev) => (prev === patient._id ? null : patient._id))
                    }
                    onPageChange={handleListPageChange}
                    onRowsPerPageChange={handleListRowsPerPageChange}
                  />
                  {batchTruncated && (
                    <div className={classes.filterNote}>
                      Filtered from the top {patientsList.length} of {patientsCount} matches.
                      Refine your search to see more.
                    </div>
                  )}
                </div>
                {selectedPatient && (
                  <div className={classes.detailPane}>
                    <GlobalSearchPatientDetail
                      patient={selectedPatient}
                      onClose={() => setSelectedId(null)}
                      onNavigate={navigate}
                    />
                  </div>
                )}
              </div>
            )}
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
